/**
 * @fileoverview Report Templates Settings (MOR-556)
 *
 * Purpose: Upload firm-level Word report templates (.docx/.dotx). An AI agent
 * scans each uploaded report into a structured section outline, which the user
 * can then edit. PDFs are rejected because styled export needs the source
 * OOXML. Templates are reusable across jobs as the blueprint for generated
 * reports. Library view (gallery / table) mirrors Settings → Docs; clicking a
 * template opens its outline editor.
 * URL: /dashboard/settings/report-templates
 */

import { createFileRoute } from "@tanstack/react-router";
import { useUploadFiles } from "@xixixao/uploadstuff/react";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "~/convex/_generated/api";
import type { Id } from "~/convex/_generated/dataModel";
import { useActiveOrg } from "@/utils/org-context";
import { Badge, Button, Input, Spinner } from "@/ui/primitives";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/ui/composed/overlays/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  AlertCircle,
  ArrowLeft,
  Check,
  Download,
  FileText,
  LayoutGrid,
  List,
  Loader2,
  Pencil,
  ScrollText,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { extractErrorMessage } from "@/utils/errors";
import { cn } from "@/utils/misc";
import { ReportTemplateEditor } from "@/components/settings/reports/ReportTemplateEditor";
import { SourceDocPreview } from "@/components/settings/precedents/SourceDocPreview";
import { downloadSourceDoc } from "@/components/settings/precedents/SourceDocPreview.helpers";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/settings/report-templates",
)({
  component: ReportTemplatesSettings,
  beforeLoad: () => ({
    title: "Report Templates",
    headerTitle: "Settings",
  }),
});

/** Styled Word export needs the source OOXML — PDFs cannot be mapped into the firm's template. */
const WORD_REPORT_ACCEPT = ".docx,.dotx";

function isWordReportSource(file: File): boolean {
  const ext = file.name.toLowerCase().split(".").pop();
  return ext === "docx" || ext === "dotx";
}

interface ReportTemplateRow {
  _id: Id<"reportTemplates">;
  _creationTime: number;
  name: string;
  extractionStatus?: "pending" | "extracting" | "completed" | "failed";
  sectionCount: number;
  sourceDocumentName?: string;
  sourceDocumentUrl?: string | null;
}

function ReportTemplatesSettings() {
  const { orgId } = useActiveOrg();
  const typedOrgId = orgId as Id<"organizations">;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedId, setSelectedId] = useState<Id<"reportTemplates"> | null>(
    null,
  );
  const [deleteTarget, setDeleteTarget] = useState<ReportTemplateRow | null>(
    null,
  );
  const [renameTarget, setRenameTarget] = useState<ReportTemplateRow | null>(
    null,
  );
  const [renameDraft, setRenameDraft] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "table">(() => {
    if (typeof window !== "undefined") {
      return (
        (localStorage.getItem("report-template-view-mode") as
          "grid" | "table") ?? "grid"
      );
    }
    return "grid";
  });
  const toggleViewMode = useCallback((mode: "grid" | "table") => {
    setViewMode(mode);
    if (typeof window !== "undefined") {
      localStorage.setItem("report-template-view-mode", mode);
    }
  }, []);

  const { data: templates, isLoading } = useQuery({
    ...convexQuery(api.settings.reports.queries.listReportTemplates, {
      orgId: typedOrgId,
    }),
    enabled: !!orgId,
  });

  const generateUploadUrl = useConvexMutation(
    api.settings.reports.mutations.generateUploadUrl,
  );
  const { mutateAsync: createReportTemplate } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.reports.mutations.createReportTemplate,
    ),
  });
  const { mutateAsync: deleteTemplate } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.reports.mutations.deleteTemplate,
    ),
  });
  const { mutateAsync: renameTemplate } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.reports.mutations.renameTemplate,
    ),
  });

  const { startUpload } = useUploadFiles(generateUploadUrl, {
    onUploadBegin: () => setUploading(true),
    onUploadComplete: async (uploaded) => {
      if (fileInputRef.current) fileInputRef.current.value = "";
      const results: Array<
        { ok: true; id: Id<"reportTemplates"> } | { ok: false }
      > = [];
      for (const file of uploaded) {
        try {
          const storageId = (file.response as { storageId: string })
            .storageId as Id<"_storage">;
          const fileName = file.name ?? "report";
          const baseName = fileName.replace(/\.[^.]+$/, "");
          const id = await createReportTemplate({
            orgId: typedOrgId,
            name: baseName,
            storageId,
            fileName,
          });
          results.push({ ok: true, id });
        } catch {
          results.push({ ok: false });
        }
      }
      const succeeded = results.filter((r) => r.ok).length;
      const failed = results.length - succeeded;
      const createdId =
        [...results]
          .reverse()
          .find((r): r is { ok: true; id: Id<"reportTemplates"> } => r.ok)
          ?.id ?? null;
      setUploading(false);
      setAddOpen(false);
      if (succeeded > 0) {
        toast.success(
          succeeded === 1
            ? "Uploaded — scanning the report…"
            : `Uploaded ${succeeded} reports — scanning…`,
        );
        // Jump straight into the editor when a single report was added.
        if (succeeded === 1 && createdId) setSelectedId(createdId);
      }
      if (failed > 0) {
        toast.error(
          `${failed} report${failed === 1 ? "" : "s"} failed to upload`,
        );
      }
    },
    onUploadError: () => {
      setUploading(false);
      toast.error("Upload failed");
    },
  });

  const handleAddFiles = useCallback(
    (files: File[]) => {
      const accepted = files.filter(isWordReportSource);
      const rejected = files.length - accepted.length;
      if (rejected > 0) {
        toast.error(
          rejected === files.length
            ? "Please upload a Word file (.docx or .dotx). PDFs cannot be exported into the firm's template."
            : `${rejected} file${rejected === 1 ? "" : "s"} skipped — Word (.docx / .dotx) only.`,
        );
      }
      if (accepted.length > 0) startUpload(accepted);
    },
    [startUpload],
  );

  const handleFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleAddFiles(Array.from(e.target.files));
      }
    },
    [handleAddFiles],
  );

  const rows: ReportTemplateRow[] = (templates ?? []) as ReportTemplateRow[];
  const selected = rows.find((r) => r._id === selectedId) ?? null;

  // Keep the rename buffer in sync with the selected template. Keyed on the
  // scalar id/name (not the `selected` object, whose identity changes every
  // render) so typing in the rename field isn't clobbered.
  const selectedName = selected?.name;
  useEffect(() => {
    if (selectedName !== undefined) setNameDraft(selectedName);
  }, [selected?._id, selectedName]);

  const commitRename = useCallback(async () => {
    if (!selected) return;
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === selected.name) {
      setNameDraft(selected.name);
      return;
    }
    try {
      await renameTemplate({ templateId: selected._id, name: trimmed });
      toast.success("Renamed");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to rename"));
      setNameDraft(selected.name);
    }
  }, [selected, nameDraft, renameTemplate]);

  const openRename = useCallback((t: ReportTemplateRow) => {
    setRenameTarget(t);
    setRenameDraft(t.name);
  }, []);

  const commitLibraryRename = useCallback(async () => {
    if (!renameTarget) return;
    const trimmed = renameDraft.trim();
    if (!trimmed || trimmed === renameTarget.name) {
      setRenameTarget(null);
      return;
    }
    setRenaming(true);
    try {
      await renameTemplate({ templateId: renameTarget._id, name: trimmed });
      toast.success("Renamed");
      setRenameTarget(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to rename"));
    } finally {
      setRenaming(false);
    }
  }, [renameTarget, renameDraft, renameTemplate]);

  const hiddenInput = (
    <input
      ref={fileInputRef}
      type="file"
      multiple
      accept={WORD_REPORT_ACCEPT}
      className="hidden"
      onChange={handleFile}
    />
  );

  // ---- Detail view ----------------------------------------------------------
  if (selectedId) {
    return (
      <div className="w-full">
        {hiddenInput}
        <div className="mb-4 flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedId(null)}
            className="text-muted-foreground"
          >
            <ArrowLeft className="mr-1 size-4" />
            Report Library
          </Button>
          {selected?.sourceDocumentUrl && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPreviewOpen(true)}
            >
              <FileText className="mr-1 size-3.5" />
              View source document
            </Button>
          )}
        </div>
        {selected && (
          <input
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
            }}
            aria-label="Report template name"
            className="mb-4 w-full rounded-md border border-transparent bg-transparent px-2 py-1 text-xl font-semibold outline-none hover:border-border focus:border-border focus:bg-background"
          />
        )}
        <ReportTemplateEditor templateId={selectedId} />

        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-4xl">
            <DialogHeader className="flex-row items-center justify-between gap-2 pr-8">
              <DialogTitle className="truncate">
                {selected?.sourceDocumentName ?? selected?.name ?? "Document"}
              </DialogTitle>
              {selected?.sourceDocumentUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (selected?.sourceDocumentUrl) {
                      downloadSourceDoc(
                        selected.sourceDocumentUrl,
                        selected.sourceDocumentName,
                      );
                    }
                  }}
                >
                  <Download className="mr-1 size-3.5" />
                  Download
                </Button>
              )}
            </DialogHeader>
            {selected?.sourceDocumentUrl ? (
              <div className="max-h-[80vh] overflow-auto">
                <SourceDocPreview
                  url={selected.sourceDocumentUrl}
                  fileName={selected.sourceDocumentName}
                  title={selected.name}
                />
              </div>
            ) : (
              <div className="py-12 text-center text-sm text-muted-foreground">
                No source document available.
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ---- Library view ---------------------------------------------------------
  return (
    <div className="mx-auto w-full max-w-6xl p-6">
      {hiddenInput}

      <div className="rounded-xl border bg-background p-5 shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold">Report Library</h1>
            <p className="mt-1 text-sm text-primary/60">
              Upload examples of reports your firm has used. Morgan scans the
              report and creates reusable section outlines for use on report
              generation on jobs.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Badge variant="secondary">
              {rows.length} template{rows.length !== 1 ? "s" : ""}
            </Badge>
            <div className="flex items-center rounded-md border border-border/60">
              <button
                type="button"
                onClick={() => toggleViewMode("grid")}
                className={cn(
                  "flex items-center justify-center rounded-l-md px-2 py-1.5 transition-colors",
                  viewMode === "grid"
                    ? "bg-primary/10 text-primary"
                    : "text-primary/40 hover:text-primary/60",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="size-3.5" />
              </button>
              <button
                type="button"
                onClick={() => toggleViewMode("table")}
                className={cn(
                  "flex items-center justify-center rounded-r-md px-2 py-1.5 transition-colors",
                  viewMode === "table"
                    ? "bg-primary/10 text-primary"
                    : "text-primary/40 hover:text-primary/60",
                )}
                aria-label="Table view"
              >
                <List className="size-3.5" />
              </button>
            </div>
            <Button
              onClick={() => setAddOpen(true)}
              disabled={uploading}
              size="sm"
            >
              {uploading ? (
                <Loader2 className="mr-1 size-4 animate-spin" />
              ) : (
                <Upload className="mr-1 size-4" />
              )}
              Add Reports
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed py-16 text-muted-foreground">
            <ScrollText className="size-8" />
            <p className="text-sm">No report templates yet.</p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setAddOpen(true)}
            >
              <Upload className="mr-1 size-4" />
              Add a report
            </Button>
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((t) => (
              <button
                key={t._id}
                type="button"
                onClick={() => setSelectedId(t._id)}
                className="group flex flex-col gap-3 rounded-xl border bg-background p-4 text-left shadow-[0_1px_3px_rgba(0,0,0,0.06)] transition-shadow hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)]"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <ScrollText className="size-4" />
                  </div>
                  <div className="flex items-center gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                    <Pencil
                      className="size-3.5 text-muted-foreground hover:text-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        openRename(t);
                      }}
                      aria-label="Rename"
                    />
                    <Trash2
                      className="size-3.5 text-muted-foreground hover:text-red-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(t);
                      }}
                      aria-label="Delete"
                    />
                  </div>
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{t.name}</p>
                  {t.sourceDocumentName && (
                    <p className="truncate text-xs text-muted-foreground">
                      {t.sourceDocumentName}
                    </p>
                  )}
                </div>
                <StatusPill status={t.extractionStatus} />
              </button>
            ))}
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border bg-background">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/60 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium">Name</th>
                  <th className="px-4 py-2.5 text-left font-medium">
                    Sections
                  </th>
                  <th className="px-4 py-2.5 text-left font-medium">Status</th>
                  <th className="px-4 py-2.5 text-left font-medium">Added</th>
                  <th className="px-4 py-2.5 text-right font-medium">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr
                    key={t._id}
                    className="cursor-pointer border-b last:border-0 hover:bg-muted/40"
                    onClick={() => setSelectedId(t._id)}
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <ScrollText className="size-4 shrink-0 text-muted-foreground" />
                        <span className="font-medium">{t.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">
                      {t.sectionCount}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusPill status={t.extractionStatus} />
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">
                      {new Date(t._creationTime).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            openRename(t);
                          }}
                          aria-label="Rename"
                        >
                          <Pencil className="size-4" />
                        </button>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-red-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(t);
                          }}
                          aria-label="Delete"
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog
        open={addOpen}
        onOpenChange={(open) => {
          if (uploading) return;
          setAddOpen(open);
          if (!open) setIsDragOver(false);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Reports</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Upload Word reports your firm has produced. Morgan scans each one
            into a reusable section outline that drives report generation on
            jobs.
          </p>
          <div
            onDrop={(e) => {
              e.preventDefault();
              setIsDragOver(false);
              if (uploading) return;
              handleAddFiles(Array.from(e.dataTransfer.files));
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (!uploading) setIsDragOver(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsDragOver(false);
            }}
            onClick={() => {
              if (!uploading) fileInputRef.current?.click();
            }}
            className={cn(
              "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 transition-colors",
              uploading && "pointer-events-none opacity-60",
              isDragOver
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/40",
            )}
          >
            {uploading ? (
              <Loader2 className="size-7 animate-spin text-primary/50" />
            ) : (
              <Upload className="size-7 text-primary/40" />
            )}
            <div className="text-center">
              <p className="text-sm font-medium">
                {uploading
                  ? "Uploading…"
                  : "Drag and drop files here, or click to browse"}
              </p>
              <p className="text-xs text-muted-foreground">.docx and .dotx</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!renameTarget}
        onOpenChange={(open) => !open && setRenameTarget(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rename report template</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void commitLibraryRename();
            }}
            className="space-y-4"
          >
            <Input
              autoFocus
              value={renameDraft}
              onChange={(e) => setRenameDraft(e.target.value)}
              placeholder="Report template name"
              aria-label="Report template name"
            />
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRenameTarget(null)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={renaming || !renameDraft.trim()}>
                {renaming && <Loader2 className="mr-1 size-4 animate-spin" />}
                Save
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete report template?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.name}" and its section outline will be permanently
              deleted. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!deleteTarget) return;
                try {
                  await deleteTemplate({ templateId: deleteTarget._id });
                  if (selectedId === deleteTarget._id) setSelectedId(null);
                  toast.success(`"${deleteTarget.name}" deleted`);
                } catch (err) {
                  toast.error(extractErrorMessage(err, "Failed to delete"));
                } finally {
                  setDeleteTarget(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatusPill({
  status,
}: {
  status?: "pending" | "extracting" | "completed" | "failed";
}) {
  if (status === "pending" || status === "extracting") {
    return (
      <Badge color="blue" size="sm" className="w-fit shrink-0">
        <Loader2 className="mr-1 size-3 animate-spin" />
        Scanning
      </Badge>
    );
  }
  if (status === "failed") {
    return (
      <Badge color="red" size="sm" className="w-fit shrink-0">
        <AlertCircle className="mr-1 size-3" />
        Failed
      </Badge>
    );
  }
  return (
    <Badge color="green" size="sm" className="w-fit shrink-0">
      <Check className="mr-1 size-3" />
      Ready
    </Badge>
  );
}
